import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { Share2, X } from "lucide-react";
import { useAuth } from "../../stores/auth";
import { useMobileChrome } from "../../stores/mobileChrome";
import { api } from "../../lib/api";
import { socialLinks } from "./socialLinks";

const HIDDEN_PREFIXES = ["/checkout", "/flexxi-kyc/capture"];

function bottomClass(navHidden: boolean, stacked: boolean): string {
  if (navHidden) return stacked ? "bottom-[4.75rem] lg:bottom-[4.75rem]" : "bottom-4 lg:bottom-4";
  return stacked
    ? "bottom-[calc(var(--mobile-nav-height)+4.5rem+env(safe-area-inset-bottom))] lg:bottom-[4.75rem]"
    : "bottom-[calc(var(--mobile-nav-height)+0.75rem+env(safe-area-inset-bottom))] lg:bottom-4";
}

export function SocialFab() {
  const { user, config, moduleEnabled } = useAuth();
  const location = useLocation();
  const navHidden = useMobileChrome((s) => s.bottomNavSuppressed > 0);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const aiChatPossible = Boolean(user) && moduleEnabled("ai_chat_fe");
  const { data: aiStatus } = useQuery({
    queryKey: ["ai-status"],
    queryFn: () => api<{ feReady: boolean }>("/ai/status"),
    staleTime: 300_000,
    enabled: aiChatPossible,
  });
  const stacked = aiChatPossible && Boolean(aiStatus?.feReady);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const links = socialLinks(config?.social, config?.whatsappUrl);
  const hiddenRoute = HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p));
  if (!config?.social?.floatingButtonEnabled || links.length === 0 || hiddenRoute) return null;

  return (
    <div ref={rootRef} className={`fixed right-4 z-40 flex flex-col items-end gap-2 lg:right-4 ${bottomClass(navHidden, stacked)}`}>
      <AnimatePresence>
        {open &&
          links.map((link, i) => (
            <motion.a
              key={link.key}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 12, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.9 }}
              transition={{ delay: (links.length - 1 - i) * 0.04, duration: 0.18 }}
              className="flex items-center gap-2"
              aria-label={link.label}
            >
              <span className="rounded-full bg-black/70 px-2.5 py-1 text-xs font-semibold text-white shadow-lg backdrop-blur">
                {link.label}
              </span>
              <span
                className={`flex h-11 w-11 items-center justify-center rounded-full text-white shadow-lg shadow-black/30 ${link.bgClass}`}
              >
                <link.Icon className="h-5 w-5" />
              </span>
            </motion.a>
          ))}
      </AnimatePresence>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? "Close social links" : "Contact us"}
        className="flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366] text-white shadow-xl shadow-black/30 transition-transform hover:scale-105 active:scale-95"
      >
        {open ? <X className="h-5 w-5" aria-hidden /> : <Share2 className="h-5 w-5" aria-hidden />}
      </button>
    </div>
  );
}
